import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { useAppDispatch, useAppSelector } from "../../redux/app/hooks";
import { visibility } from "../../redux/slices/rightSideAd";
import { Tooltip } from "@mui/material";
import ToasterWithMessage from "../../components/toaster";
import TabsContentHeader from "../../components/AdminDashboardTabsContentHeader/TabsContentHeader";
import MobileScreenHeader from "../../components/MobileScreenHeader/MobileScreenHeader";
import MaterialActionsOverlay, { MenuItemType } from "../../components/ActionsOverlay/MaterialActionsOverlay";
import Loader from "../../components/Loader/Loader";
import images from "../../assets/images";
import Select, { StylesConfig, components } from "react-select";
import { GET_ALL_PAYMENT_ORDERS, UPDATE_PAYMENT_RECORD_STATUS, GET_ALL_PAYMENT_STATUS_TYPES } from "./api/AdminPaymentsQuery";
import { GET_PROGRAMME_CATALOGUE } from "../AdminLoginPage/api/AdminLoginQuery";
import { getScreenDimensions, parseAllowedStatuses } from "../../utils/helper";
import { VIRTUAL } from "../../utils/constants";
import "./AdminPayments.scss";

interface StudentRegistrationNode {
  srCommunicationEmail: string | null;
  srCommunicationPhone: string | null;
  srGuardianName: string | null;
  srDetails?: any;
}

interface PaymentOrderNode {
  pmtPOrderId: string | null;
  pmtMOrderId: string | null;
  pmtId: string;
  prId: string;
  sId: string;
  studentBySId: {
    userByUId: {
      uFullName: string | null;
      uFirstName: string | null;
      uEmailId: string | null;
      uCognitoId: string | null;
    } | null;
    studentRegistrationsBySId: {
      nodes: StudentRegistrationNode[];
    } | null;
  } | null;
  paymentStatusTypeByPstId: {
    pstName: string | null;
    pstId: string | null;
  } | null;
}

const CheckboxOption = (props: any) => {
  return (
    <components.Option {...props}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
        <input
          type="checkbox"
          checked={props.isSelected}
          readOnly
          style={{
            cursor: "pointer",
            width: "16px",
            height: "16px",
            accentColor: "#0b74bb",
            margin: 0
          }}
        />
        <span style={{ fontSize: "13px", fontFamily: "Inter", color: "#333333" }}>{props.label}</span>
      </div>
    </components.Option>
  );
};

const CustomValueContainer = ({ children, ...props }: any) => {
  const cleanChildren = React.Children.toArray(children).filter((child: any) => {
    return child?.type?.name !== "MultiValue" && child?.props?.components?.MultiValue === undefined;
  });
  const isMobile = getScreenDimensions()?.isMobileScreen;

  return (
    <components.ValueContainer {...props}>
      {!isMobile && (
        <span className="filter-label-text" style={{ 
          color: "#333", 
          fontSize: "13px", 
          fontWeight: "500",
          fontFamily: "Inter",
          whiteSpace: "nowrap"
        }}>
          Status
        </span>
      )}
      {isMobile && (
        <span className="filter-icon-symbol" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1.5 2.5H14.5L9.5 8.5V13.5L6.5 15V8.5L1.5 2.5Z" stroke="#0b74bb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      )}
      {cleanChildren}
    </components.ValueContainer>
  );
};

const CustomDropdownIndicator = (props: any) => {
  const isMobile = getScreenDimensions()?.isMobileScreen;
  if (isMobile) return null;
  return <components.DropdownIndicator {...props} />;
};


const AdminPayments: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { MoreIcon, bookIcon, greyArrow, UpArrow, DownArrow, searchCloseIcon } = images;
  const { success, error } = ToasterWithMessage();

  // Redux state for mobile header
  const role = useAppSelector((state) => state.auth.role);
  const schoolType = useAppSelector((state) => state.auth.schoolType);
  const profile = useAppSelector((state) => state.auth.adminProfile);
  const profileImage = useAppSelector((state) => state?.auth?.adminProfileImage);
  const studentName = profile?.getCurrentUser?.nodes?.[0]?.uFullName;

  // Protect route for Virtual School Admin only
  useEffect(() => {
    if (schoolType !== VIRTUAL) {
      navigate("/admin-dashboard", { replace: true });
    }
  }, [schoolType, navigate]);

  // Ensure right sidebar ad is visible
  useEffect(() => {
    dispatch(visibility({ isVisible: true }));
  }, [dispatch]);

  // State
  const [searchValue, setSearchValue] = useState("");
  const [isSearchClick, setIsSearchClick] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string[]>(["ALL"]);
  const itemsPerPage = 20;

  // Table scroll and fades state
  const [scrollState, setScrollState] = useState({ left: false, right: false });
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Expanded Row State
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  // Action Menu State & Constants
  const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOrderForAction, setSelectedOrderForAction] = useState<PaymentOrderNode | null>(null);

  const handleActionClick = (e: React.MouseEvent<HTMLElement>, order: PaymentOrderNode) => {
    e.stopPropagation();
    setActionAnchorEl(e.currentTarget);
    setSelectedOrderForAction(order);
  };

  const handleActionClose = () => {
    setActionAnchorEl(null);
    setSelectedOrderForAction(null);
  };

  const handleRowClick = (orderId: string) => {
    setExpandedOrderId(prev => prev === orderId ? null : orderId);
  };

  // Overflow arrays for tooltips
  const [isOrderOverflow, setIsOrderOverflow] = useState<boolean[]>([]);
  const [isStudentOverflow, setIsStudentOverflow] = useState<boolean[]>([]);
  const [isProgramOverflow, setIsProgramOverflow] = useState<boolean[]>([]);
  const [isEmailOverflow, setIsEmailOverflow] = useState<boolean[]>([]);

  // Refs for checking text overflow
  const orderRefs = useRef<(HTMLDivElement | null)[]>([]);
  const studentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const programRefs = useRef<(HTMLDivElement | null)[]>([]);
  const emailRefs = useRef<(HTMLDivElement | null)[]>([]);

  const filterObject = useMemo(() => {
    const searchInput = searchValue.trim() ? `%${searchValue.trim()}%` : "%";
    const baseFilter: any = {
      deletedAt: { isNull: true },
      or: [
        { studentBySId: { userByUId: { uFullName: { likeInsensitive: searchInput } } } },
        { studentBySId: { studentRegistrationsBySId: { every: { srCommunicationEmail: { likeInsensitive: searchInput } } } } },
        { pmtPOrderId: { likeInsensitive: searchInput } }
      ]
    };

    const includesAll = statusFilter.includes("ALL");
    if (includesAll) {
      return baseFilter;
    } else {
      const statusList = statusFilter.filter(s => s !== "ALL").map(s => s.toUpperCase());
      return {
        ...baseFilter,
        paymentStatusTypeByPstId: {
          pstName: {
            in: statusList
          }
        }
      };
    }
  }, [searchValue, statusFilter]);

  // Queries & Mutations
  const { data: paymentData, loading: paymentLoading, refetch: refetchPayments } = useQuery(GET_ALL_PAYMENT_ORDERS, {
    fetchPolicy: "no-cache",
    variables: {
      first: itemsPerPage,
      offset: (currentPage - 1) * itemsPerPage,
      filter: filterObject
    },
    onError: (err) => {
      error(err?.message || "Failed to load payment orders.");
    }
  });

  const [updatePaymentRecordStatus, { loading: updatingStatus }] = useMutation(UPDATE_PAYMENT_RECORD_STATUS, {
    onCompleted: (data) => {
      const message = data?.updatePaymentRecordStatus?.message || "Payment status updated successfully!";
      success(message);
      setCurrentPage(1);
      refetchPayments();
    },
    onError: (err) => {
      error(err?.message || "Failed to update payment status.");
    }
  });

  const handlePaymentStatusUpdate = useCallback((order: PaymentOrderNode, action: string) => {
    if (!order?.pmtId) return;
    updatePaymentRecordStatus({
      variables: {
        input: {
          pmtId: order.pmtId,
          action: action
        }
      }
    });
  }, [updatePaymentRecordStatus]);

  const { data: programmeData, loading: programmeLoading } = useQuery(GET_PROGRAMME_CATALOGUE, {
    fetchPolicy: "no-cache"
  });

  const { data: statusTypesData } = useQuery(GET_ALL_PAYMENT_STATUS_TYPES, {
    fetchPolicy: "no-cache"
  });

  const paymentOrders: PaymentOrderNode[] = useMemo(() => {
    return paymentData?.allPaymentOrders?.nodes || [];
  }, [paymentData]);

  const programmeList = useMemo(() => {
    return programmeData?.programmeCatalogue || [];
  }, [programmeData]);

  // Removed actions menu as table now expands inline

  // Tooltip overflow checks
  const checkOrderOverflow = (index: number) => {
    const el = orderRefs.current[index];
    if (el) {
      const overflow = el.scrollWidth > el.clientWidth;
      setIsOrderOverflow(prev => {
        const next = [...prev];
        next[index] = overflow;
        return next;
      });
    }
  };

  const checkStudentOverflow = (index: number) => {
    const el = studentRefs.current[index];
    if (el) {
      const overflow = el.scrollWidth > el.clientWidth;
      setIsStudentOverflow(prev => {
        const next = [...prev];
        next[index] = overflow;
        return next;
      });
    }
  };

  const checkProgramOverflow = (index: number) => {
    const el = programRefs.current[index];
    if (el) {
      const overflow = el.scrollWidth > el.clientWidth;
      setIsProgramOverflow(prev => {
        const next = [...prev];
        next[index] = overflow;
        return next;
      });
    }
  };

  const checkEmailOverflow = (index: number) => {
    const el = emailRefs.current[index];
    if (el) {
      const overflow = el.scrollWidth > el.clientWidth;
      setIsEmailOverflow(prev => {
        const next = [...prev];
        next[index] = overflow;
        return next;
      });
    }
  };

  // Local filtering & searching on payment records
  const filteredOrders = paymentOrders;

  const handleSearchClick = () => {
    setIsSearchClick(true);
  };

  const handleCloseSearch = () => {
    setIsSearchClick(false);
    setSearchValue("");
    handleSearchDebounce("");
  };

  const searchOnChange = (event: { target: { value: string } }) => {
    setSearchValue(event.target.value);
    handleSearchDebounce(event.target.value);
  };

  // Handle Search Input Change
  const handleSearchDebounce = useCallback((value: string) => {
    setSearchValue(value);
    setCurrentPage(1); // Reset to page 1 on search
  }, []);

  // Pagination bounds & slice
  const totalPages = Math.ceil((paymentData?.allPaymentOrders?.totalCount || 0) / itemsPerPage) || 1;
  
  const paginatedOrders = paymentOrders;

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const showMax = 5;

    if (totalPages <= showMax) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }

      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const formatPaymentsCount = (count: number) => {
    if (count === 0) return "No Payments";
    if (count === 1) return "1 Payment Order";
    return `${count} Payment Orders`;
  };

  const handleScroll = () => {
    const el = containerRef.current;
    if (el) {
      const left = el.scrollLeft > 5;
      const right = el.scrollLeft < el.scrollWidth - el.clientWidth - 5;
      setScrollState({ left, right });
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      handleScroll();
    }, 100);

    window.addEventListener("resize", handleScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", handleScroll);
    };
  }, [paginatedOrders]);

  // Collapse expanded row when changing page, search query, or status filter
  useEffect(() => {
    setExpandedOrderId(null);
  }, [currentPage, searchValue, statusFilter, isSearchClick]);

  const handleStatusFilterChange = (selected: any) => {
    const newValues: string[] = selected ? selected.map((opt: any) => opt.value) : [];
    const wasAllChecked = statusFilter.includes("ALL");
    const isAllCheckedNow = newValues.includes("ALL");

    if (isAllCheckedNow && !wasAllChecked) {
      // Case 1: "All Status" was just clicked/selected (clear other options)
      setStatusFilter(["ALL"]);
    } else if (!isAllCheckedNow && wasAllChecked && newValues.length === 0) {
      // Case 2: "All Status" was already checked, clicking it again does not uncheck
      setStatusFilter(["ALL"]);
    } else {
      // Case 3: Sub-options are active, so remove "ALL"
      const nextFilter = newValues.filter(val => val !== "ALL");
      if (nextFilter.length === 0) {
        setStatusFilter(["ALL"]);
      } else {
        setStatusFilter(nextFilter);
      }
    }
    setCurrentPage(1);
  };

  const formatStatusLabel = useCallback((name: string | null | undefined): string => {
    if (!name) return "";
    return name
      .toLowerCase()
      .split(" ")
      .map((word) => (word === "3ds" ? "3DS" : word.charAt(0).toUpperCase() + word.slice(1)))
      .join(" ");
  }, []);

  const statusOptions = useMemo(() => {
    const defaultOption = [{ value: "ALL", label: "All Status" }];
    const nodes = statusTypesData?.allPaymentStatusTypes?.nodes || [];
    const dynamicOptions = nodes.map((node: { pstId: string; pstName: string }) => {
      const name = node.pstName || "";
      return {
        value: name.toUpperCase(),
        label: formatStatusLabel(name)
      };
    });
    return [...defaultOption, ...dynamicOptions];
  }, [statusTypesData, formatStatusLabel]);

  const isMobile = getScreenDimensions()?.isMobileScreen;

  const customSelectStyles: StylesConfig<any, true> = {
    control: (styles, { isFocused }) => ({
      ...styles,
      cursor: "pointer",
      borderRadius: "6px",
      width: isMobile ? "34px" : "200px",
      minHeight: isMobile ? "34px" : "30px",
      height: isMobile ? "34px" : "auto",
      padding: "0",
      border: isFocused ? "1px solid #0b74bb" : "1px solid #86c3eb",
      boxShadow: isFocused ? "0 0 0 2px rgba(11, 116, 187, 0.15)" : "none",
      backgroundColor: "#ffffff",
      fontFamily: "Inter",
      fontSize: "13px",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      justifyContent: isMobile ? "center" : "space-between",
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: "#0b74bb",
      }
    }),
    multiValue: (styles) => ({
      ...styles,
      display: "none"
    }),
    valueContainer: (styles) => ({
      ...styles,
      padding: isMobile ? "0" : "0px 8px",
      display: "flex",
      alignItems: "center",
      justifyContent: isMobile ? "center" : "flex-start",
      flexWrap: "nowrap",
      overflow: "hidden"
    }),
    indicatorSeparator: (styles) => ({
      ...styles,
      display: "none"
    }),
    indicatorsContainer: (styles) => ({
      ...styles,
      padding: isMobile ? "0" : "0px 8px",
      display: isMobile ? "none" : "flex"
    }),
    clearIndicator: (styles) => ({
      ...styles,
      padding: "0px"
    }),
    input: (styles) => ({
      ...styles,
      fontFamily: "Inter",
      fontWeight: "400",
      fontSize: "13px",
      margin: "0",
      padding: "0"
    }),
    placeholder: (styles) => ({
      ...styles,
      fontFamily: "Inter",
      fontWeight: "500",
      fontSize: "13px",
      color: "#b1b1b1"
    }),
    option: (styles, { isFocused, isSelected }) => ({
      ...styles,
      backgroundColor: isFocused ? "#e8f4fd" : "#ffffff",
      color: "#333333",
      fontFamily: "Inter",
      fontWeight: isSelected ? "600" : "400",
      fontSize: "13px",
      cursor: "pointer",
      padding: "6px 12px",
      display: "flex",
      alignItems: "center",
      "&:active": {
        backgroundColor: "#e8f4fd",
      }
    }),
    singleValue: (styles) => ({
      ...styles,
      color: "#333333",
      fontFamily: "Inter",
      fontWeight: "400",
      fontSize: "13px"
    }),
    dropdownIndicator: (styles) => ({
      ...styles,
      color: "#7a828a",
      padding: "0",
      display: isMobile ? "none" : "flex",
      "&:hover": {
        color: "#0b74bb"
      }
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "6px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      border: "1px solid #e1e3e5",
      zIndex: 9999,
      minWidth: "200px",
      right: 0,
      left: "auto"
    }),
    menuList: (provided) => ({
      ...provided,
      padding: "4px 0"
    })
  };

  const isLoading = paymentLoading || programmeLoading || updatingStatus;

  if (schoolType !== VIRTUAL) {
    return null;
  }

  return (
    <div className="AdminPayments">
      {getScreenDimensions()?.isMobileScreen && isSearchClick ? (
        <div className="adminPaymentsSearch">
          <input
            id="searchBox"
            type="text"
            placeholder="Search"
            onChange={searchOnChange}
            value={searchValue}
            className="SearchInputFieldStyle"
          />
          {getScreenDimensions()?.isMobileScreen && isSearchClick && (
            <img
              src={searchCloseIcon}
              alt="closeIcon"
              className="closeIcon"
              onClick={handleCloseSearch}
            />
          )}
        </div>
      ) : (
        <>
          {getScreenDimensions()?.isMobileScreen && (
            <MobileScreenHeader
              profileImage={profileImage}
              role={role}
              studentName={studentName}
              profile={profile}
              handleSearchClick={handleSearchClick}
            />
          )}
          <TabsContentHeader
            title="Payment List"
            subtitle={formatPaymentsCount(paymentData?.allPaymentOrders?.totalCount || 0)}
            addLabel=""
            subImage={bookIcon}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            handleSearchDebounce={handleSearchDebounce}
            filterComponent={
              <div className="payment-toolbar">
                <div className="filter-group">
                  <Select
                    isMulti
                    isClearable={false}
                    placeholder="Status"
                    value={statusOptions.filter(opt => statusFilter.includes(opt.value))}
                    onChange={handleStatusFilterChange}
                    options={statusOptions}
                    styles={customSelectStyles}
                    isSearchable={false}
                    hideSelectedOptions={false}
                    closeMenuOnSelect={false}
                    components={{
                      Option: CheckboxOption,
                      ValueContainer: CustomValueContainer,
                      DropdownIndicator: CustomDropdownIndicator,
                      Placeholder: () => null
                    }}
                  />
                </div>
              </div>
            }
          />
        </>
      )}

      {isLoading ? (
        <Loader />
      ) : (
        <div className={`table-scroll-wrapper ${scrollState.left && filteredOrders.length > 0 ? "show-left-fade" : ""} ${scrollState.right && filteredOrders.length > 0 ? "show-right-fade" : ""}`}>
          {filteredOrders.length > 0 ? (
            <>
              <div
                className={`programs-table-container ${filteredOrders.length === 0 ? "noData" : ""}`}
                ref={containerRef}
                onScroll={handleScroll}
              >
                <table className="programs-table">
                <thead>
                  <tr>
                    <th className="expand-header-cell"></th>
                    <th>Order ID</th>
                    <th>Student Name</th>
                    <th>Communication Email</th>
                    <th>Status</th>
                    <th className="more-header-cell"></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedOrders.map((order, index) => {
                    const orderId = order.pmtPOrderId || order.pmtMOrderId || "N/A";
                    const studentName = order.studentBySId?.userByUId?.uFullName || "-";
                    
                    const matchedProg = programmeList.find((p: any) => p.programmeId === order.prId);
                    const programName = matchedProg?.name || "Virtual Classroom Course";
                    
                    const communicationEmail = 
                      order.studentBySId?.studentRegistrationsBySId?.nodes?.[0]?.srCommunicationEmail || 
                      order.studentBySId?.userByUId?.uEmailId || 
                      "-";
                    
                    const phoneNumber = order.studentBySId?.studentRegistrationsBySId?.nodes?.[0]?.srCommunicationPhone || "-";
                    const statusName = order.paymentStatusTypeByPstId?.pstName || "-";
                    const isExpanded = expandedOrderId === order.pmtId;
                    const academicLevel = order.studentBySId?.studentRegistrationsBySId?.nodes?.[0]?.srDetails?.currentLevel || "-";

                    return (
                      <React.Fragment key={order.pmtId || index}>
                        <tr 
                          onClick={() => handleRowClick(order.pmtId)}
                          className={`clickable-row ${isExpanded ? "expanded-active" : ""}`}
                          style={{ cursor: "pointer" }}
                        >
                          {/* Expand/Collapse Arrow Column (Front of the row) */}
                          <td className="expand-cell" onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(order.pmtId);
                          }}>
                            <img 
                              src={DownArrow} 
                              alt="toggle" 
                              className={`row-expand-arrow ${isExpanded ? "rotated" : ""}`} 
                            />
                          </td>

                          {/* Order ID */}
                          <td>
                            <Tooltip title={orderId} disableHoverListener={!isOrderOverflow[index]} placement="top">
                              <div
                                className="order-id-cell"
                                ref={(el) => { orderRefs.current[index] = el; }}
                                onMouseEnter={() => checkOrderOverflow(index)}
                              >
                                {orderId}
                              </div>
                            </Tooltip>
                          </td>

                          {/* Student Name */}
                          <td>
                            <Tooltip title={studentName} disableHoverListener={!isStudentOverflow[index]} placement="top">
                              <div
                                className="student-name-cell"
                                ref={(el) => { studentRefs.current[index] = el; }}
                                onMouseEnter={() => checkStudentOverflow(index)}
                              >
                                {studentName}
                              </div>
                            </Tooltip>
                          </td>

                          {/* Communication Email */}
                          <td>
                            <Tooltip title={communicationEmail} disableHoverListener={!isEmailOverflow[index]} placement="top">
                              <div
                                className="email-cell"
                                ref={(el) => { emailRefs.current[index] = el; }}
                                onMouseEnter={() => checkEmailOverflow(index)}
                              >
                                {communicationEmail}
                              </div>
                            </Tooltip>
                          </td>

                          {/* Payment Status */}
                          <td className="status-cell">
                            <span className={`status-badge ${statusName.toLowerCase().replace(/\s+/g, "-")}`}>
                              {statusName}
                            </span>
                          </td>

                          {/* More Action Button Column (End of the row) */}
                          <td className="actions-cell">
                            {(() => {
                              const enabledReconcileStatuses = parseAllowedStatuses(process.env.REACT_APP_ENABLE_RECONCILE_STATUSES);
                              const enabledRefundStatuses = parseAllowedStatuses(process.env.REACT_APP_ENABLE_REFUND_STATUSES);

                              const statusUpper = statusName.toUpperCase();
                              const isReconcileAllowed = enabledReconcileStatuses.includes(statusUpper);
                              const isRefundAllowed = enabledRefundStatuses.includes(statusUpper);

                              const hasAction = isReconcileAllowed || isRefundAllowed;
                              const isMoreDisabled = !hasAction;

                              return (
                                <div 
                                  className={`more-btn ${isMoreDisabled ? "disabled" : ""}`}
                                  onClick={(e) => {
                                    if (!isMoreDisabled) {
                                      handleActionClick(e, order);
                                    }
                                  }}
                                >
                                  <img src={MoreIcon} alt="more" className="more-icon" />
                                </div>
                              );
                            })()}
                          </td>
                        </tr>

                        <tr className={`expanded-row-details ${isExpanded ? "open" : ""}`}>
                          <td colSpan={6}>
                            <div className="expanded-wrapper">
                              <div className="expanded-content">
                                <div className="details-grid">
                                  <div className="details-column">
                                    <div className="detail-field">
                                      <span className="field-label">Communication Phone Number :</span>
                                      <span className="field-value">{phoneNumber}</span>
                                    </div>
                                    <div className="detail-field">
                                      <span className="field-label">Academic Level :</span>
                                      <span className="field-value">{academicLevel}</span>
                                    </div>
                                  </div>

                                  <div className="details-column">
                                    <div className="detail-field">
                                      <span className="field-label">Program Name :</span>
                                      <span className="field-value">{programName}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
              <div className="pagination">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className={currentPage === 1 ? "disableprevBtn" : "prevBtn"}
                >
                  <img src={greyArrow} alt="prev" className="paginationArrow" />
                </button>
                <div className="pageNumbers">
                  {getPageNumbers().map((page, idx) => {
                    if (page === "...") {
                      return (
                        <span key={`ellipsis-${idx}`} className="paginationEllipsis">
                          ...
                        </span>
                      );
                    }
                    return (
                      <button
                        key={`page-${page}`}
                        onClick={() => setCurrentPage(Number(page))}
                        className={`pageBtn ${currentPage === page ? "activePageBtn" : ""}`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={handleNextPage}
                  disabled={currentPage >= totalPages}
                  className={currentPage >= totalPages ? "disablenextBtn" : "nextBtn"}
                >
                  <img src={greyArrow} alt="next" className="paginationArrow nextArrow" />
                </button>
              </div>
            </>
          ) : (
            <div className="noDataFoundStyle">No data found</div>
          )}
        </div>
      )}

      <MaterialActionsOverlay
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        handleClose={handleActionClose}
        menuItems={
          selectedOrderForAction
            ? (() => {
                const statusName = (selectedOrderForAction.paymentStatusTypeByPstId?.pstName || "").toUpperCase();
                const enabledReconcileStatuses = parseAllowedStatuses(process.env.REACT_APP_ENABLE_RECONCILE_STATUSES);
                const enabledRefundStatuses = parseAllowedStatuses(process.env.REACT_APP_ENABLE_REFUND_STATUSES);

                const items = [];

                if (enabledReconcileStatuses.includes(statusName)) {
                  items.push({
                    label: "Verify & Reconcile",
                    onClick: () => {
                      const orderToUpdate = selectedOrderForAction;
                      handleActionClose();
                      if (orderToUpdate) {
                        handlePaymentStatusUpdate(orderToUpdate, "VERIFY");
                      }
                    }
                  });
                }

                if (enabledRefundStatuses.includes(statusName)) {
                  items.push({
                    label: "Mark as Refund",
                    onClick: () => {
                      const orderToUpdate = selectedOrderForAction;
                      handleActionClose();
                      if (orderToUpdate) {
                        handlePaymentStatusUpdate(orderToUpdate, "REFUND");
                      }
                    }
                  });
                }

                return items;
              })()
            : []
        }
        actionsHeight="auto"
        position="left"
        verticalPosition="bottom"
      />
    </div>
  );
};

export default AdminPayments;
